import { useState } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface AngleFilterDropdownProps {
  angles: string[];
  selectedAngle: string | null;
  onSelect: (angle: string | null) => void;
  alignRight?: boolean;
}

export function AngleFilterDropdown({
  angles,
  selectedAngle,
  onSelect,
  alignRight = false,
}: AngleFilterDropdownProps) {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleSelect = (angle: string | null) => {
    onSelect(angle);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border whitespace-nowrap",
          selectedAngle
            ? "bg-white text-fluxi border-[#E0DDFF] shadow-sm"
            : "bg-white text-gray-700 border-gray-200 shadow-sm hover:border-gray-300"
        )}
      >
        <SlidersHorizontal className="h-3.5 w-3.5 flex-shrink-0" />
        <span className={selectedAngle ? "max-w-[180px] truncate" : ""}>
          {selectedAngle || "Filtrar por ángulo de venta"}
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 flex-shrink-0 transition-transform",
            showDropdown && "rotate-180"
          )}
        />
      </button>

      <AnimatePresence>
        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-20"
              aria-hidden
              onClick={() => setShowDropdown(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 4, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.96 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className={cn(
                "absolute top-full mt-2 z-30 w-64 bg-background border border-border/40 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto",
                alignRight ? "right-0" : "left-0"
              )}
            >
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors",
                  selectedAngle === null
                    ? "bg-primary/5 text-primary font-medium"
                    : "text-foreground hover:bg-muted/50 text-gray-900 hover:bg-gray-50"
                )}
              >
                <span>Todos los ángulos</span>
              </button>

              <div className="border-t border-border/30 border-gray-200/30" />

              {angles.map((angle) => (
                <button
                  key={angle}
                  type="button"
                  onClick={() => handleSelect(angle)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors",
                    selectedAngle === angle
                      ? "bg-primary/5 text-primary font-medium"
                      : "text-foreground hover:bg-muted/50 text-gray-900 hover:bg-gray-50"
                  )}
                >
                  <span className="truncate flex-1">{angle}</span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
